import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mic, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { Mascot } from '../components/game/Mascot';
import { KidButton } from '../components/game/KidButton';
import { listenOnce, recognitionSupported, extractName, speak, stopSpeaking } from '../lib/speech';
import { unlockAudio, playTap } from '../lib/sound';

export default function SplashScreen({ onStart, voiceId, volume }) {
  const [name, setName] = useState('');
  const [listening, setListening] = useState(false);
  const [status, setStatus] = useState('');
  const recRef = useRef(null);

  useEffect(() => {
    return () => {
      if (recRef.current) recRef.current.abort && recRef.current.abort();
      stopSpeaking();
    };
  }, []);

  const handleMic = () => {
    unlockAudio();
    if (!recognitionSupported()) {
      setStatus('Seu navegador não escuta a voz. Digite seu nome! 😊');
      return;
    }
    setListening(true);
    setStatus('Ouvindo... fale seu nome! 🎤');
    recRef.current = listenOnce({
      onResult: (text) => {
        const clean = extractName(text);
        if (clean) {
          setName(clean);
          setStatus(`Entendi: ${clean}`);
          speak(`Oi ${clean}!`, { voiceId, volume });
        } else {
          setStatus('Não entendi. Tente de novo ou digite! 😊');
        }
      },
      onError: () => setStatus('Não consegui ouvir. Pode digitar! 😊'),
      onEnd: () => setListening(false),
    });
  };

  const handleStart = () => {
    const finalName = (name || '').trim();
    if (!finalName) {
      setStatus('Escreva ou fale seu nome primeiro! ✨');
      return;
    }
    unlockAudio();
    playTap();
    const pretty = finalName.charAt(0).toUpperCase() + finalName.slice(1);
    speak(`Olá ${pretty}! Vamos aprender tabuada juntos?`, { voiceId, volume });
    onStart(pretty);
  };

  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-xl flex-col items-center justify-center px-5 py-8">
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 120, damping: 12 }}
        className="mb-2"
      >
        <Mascot mood="happy" size={150} />
      </motion.div>

      <motion.h1
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="font-fred text-center text-4xl font-bold leading-tight text-indigo-900 drop-shadow sm:text-5xl"
      >
        Vamos Aprender
        <br />
        <span className="text-pink-500">Tabuada!</span>
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-2 mb-6 text-center text-lg font-bold text-purple-700"
      >
        Aprender pode ser divertido! <Sparkles className="mb-1 inline h-5 w-5 text-amber-400" />
      </motion.p>

      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="card-soft w-full p-6"
      >
        <label className="mb-2 block text-center font-fred text-lg font-semibold text-slate-700">
          Qual é o seu nome?
        </label>
        <input
          data-testid="name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleStart()}
          placeholder="Digite seu nome"
          maxLength={20}
          className="mb-4 w-full rounded-2xl border-4 border-amber-200 bg-amber-50 px-5 py-4 text-center font-fred text-2xl font-semibold text-slate-800 outline-none focus:border-amber-400"
        />

        <button
          data-testid="speak-name-btn"
          onClick={handleMic}
          disabled={listening}
          className="btn-3d mb-4 w-full bg-purple-500 px-6 py-4 text-lg text-white"
          style={{ borderColor: '#7C3AED' }}
        >
          {listening ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin" /> Ouvindo...
            </>
          ) : (
            <>
              <Mic className="h-6 w-6" /> Falar meu nome
            </>
          )}
        </button>

        <KidButton
          data-testid="start-btn"
          onClick={handleStart}
          color="#F97316"
          border="#EA580C"
          className="w-full"
        >
          Começar <ArrowRight className="h-6 w-6" />
        </KidButton>

        {status && (
          <p className="mt-3 text-center text-sm font-bold text-purple-600" data-testid="splash-status">
            {status}
          </p>
        )}
      </motion.div>
    </div>
  );
}
